import React from 'react';
import { ASSETS } from '../assets/assetMapping';
import RateLimitStatus from './RateLimitStatus';
import { FilterState } from '../types/api';
import { formatHairDisplay, formatStyleDisplay, calculateStyleCount, isFilterSelected, stringToHairArray } from '../utils/filterUtils';

interface LoadingScreenProps {
  progress: number; // 0-100
  prompt: string;
  filters: FilterState;
  rateLimitStatus?: {
    remainingUses: number;
    usageCount: number;
    resetDate: string;
    isBlocked: boolean;
    weekStart: string;
  };
  hasUploadedFile?: boolean;
  model?: string;
}

// Helper function to format filter values for display
const formatFilterValue = (value: string): string => {
  if (value === 'any') return 'Any';
  
  // Capitalize first letter of each word
  return value
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper function to format model value for display
const formatModelValue = (model: string): string => {
  const modelLabels: Record<string, string> = {
    'flux-dev': 'FLUX Dev',
    'seedream-4': 'Seedream 4.0',
    'nano-banana': 'Nano Banana'
  };
  return modelLabels[model] || 'FLUX Dev';
};


function LoadingScreen({ progress, prompt, filters, rateLimitStatus, hasUploadedFile = false, model = 'flux-dev' }: LoadingScreenProps) {
  // Convert hair to array if it's a string (for backward compatibility)
  const hairArray = Array.isArray(filters.hair) ? filters.hair : stringToHairArray(filters.hair as any);
  const styleCount = calculateStyleCount(filters.style);
  const styleDisplayText = formatStyleDisplay(filters.style);
  const hairDisplayText = formatHairDisplay(hairArray);
  
  return (
    <div className="bg-white relative size-full min-h-screen flex flex-col" data-name="Prompt2VideoAvatar">
      <div className="flex-1 content-stretch flex flex-col gap-[40px] items-center">
        {/* Header Section */}
        <div className="content-stretch flex flex-col gap-[24px] items-start relative shrink-0 w-full">
          {/* Navbar */}
          <div className="box-border content-stretch flex gap-4 items-center justify-start pb-0 pt-6 px-8 relative shrink-0 w-full max-w-[1440px] mx-auto">
           {/* BIGVU Icon */}
          <div className="h-[48px] w-[48px] relative shrink-0" data-node-id="2625:14095">
                <img alt="" className="block max-w-none size-full" src={ASSETS.BIGVU_ICON_BLUR} />
          </div> 
            
            {/* Title */}
        <div className="basis-0 font-['Inter:Bold',_sans-serif] font-bold grow leading-[0] min-h-px min-w-px not-italic relative shrink-0 text-[#253658] text-[24px] text-center hidden md:block">
          <p className="leading-[normal]">Generating your avatar</p>
        </div>
            
            {/* Right section - Credits and Avatar */}
        <div className="content-stretch flex gap-2 items-center justify-start relative shrink-0 ml-auto">
          {/* Credits - Using RateLimitStatus Component */}
          <div className="content-stretch flex flex-col gap-1 items-start justify-start relative shrink-0" data-name="Credits">
            <RateLimitStatus 
              rateLimitStatus={rateLimitStatus}
              compact={true}
              showUpgradePrompt={false}
              className="shrink-0"
            />
          </div>
        </div>
      </div>
          
          {/* Main Loading Content */}
          <div className="h-[548px] relative shrink-0 w-full">
            <div className="absolute bottom-[12px] left-0 right-0 px-8">
              <div className="max-w-[1376px] mx-auto content-stretch flex flex-col gap-[24px] items-start">
                {/* Shimmering Image Boxes */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 w-full" data-name="Options">
                  {[1, 2, 3, 4].map((index) => (
                    <div 
                      key={index} 
                      className="animate-shimmer aspect-[311/178] rounded-[15px] shrink-0 w-full overflow-hidden relative"
                    >
                      <div aria-hidden="true" className="absolute border-[2.006px] border-[rgba(123,141,177,0.08)] border-solid inset-0 pointer-events-none rounded-[15px]" />
                    </div>
                  ))}
                </div>
                
                {/* Progress Bar */}
                <div className="h-[4px] w-full relative" data-name="Loading">
                  <div className="absolute bg-[rgba(123,141,177,0.3)] inset-0 rounded-[8px]" />
                  <div 
                    className="absolute bg-gradient-to-l from-[#28b3f7] from-[0.649%] bottom-0 left-0 rounded-[8px] to-[#506aff] to-[99.351%] top-0 transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Fixed Bottom Section - Input Avatar (matches EnhancedInputAvatar) */}
      <div className="sticky bottom-0 bg-white border-t border-gray-100 p-6 z-20">
        <div className="w-full max-w-[720px] lg:max-w-[920px] mx-auto min-w-[260px]">
          {/* Main Input Container */}
          <div className="backdrop-blur-[20px] backdrop-filter bg-white box-border flex flex-col items-start justify-between p-4 relative rounded-2xl w-full min-h-[160px] lg:h-[180px]">
            <div aria-hidden="true" className="absolute border border-blue-black-30 border-solid inset-0 pointer-events-none rounded-2xl"/>
            
          {/* Prompt Text */}
            <div className="flex items-start justify-between relative shrink-0 w-full overflow-auto max-h-[80px]">
              <p className="font-inter font-normal grow leading-[1.4] min-h-px min-w-px not-italic relative text-[16px] text-blue-black-90 break-words overflow-wrap-anywhere pr-2">
                {prompt}
              </p>
            </div>

            {/* Filter Chips and Generate Button */}
            <div className="flex flex-wrap gap-2 items-end justify-start relative shrink-0 w-full">
              <div className="flex flex-wrap gap-2 grow items-center justify-between min-h-px min-w-px relative shrink-0">
                <div className="flex gap-2 items-center justify-start flex-wrap relative shrink-0">
                  {/* Attachment Button (visual only) */}
                  <div className={`box-border flex gap-1 h-8 w-8 items-center justify-center p-1 relative rounded-lg shrink-0 ${
                    hasUploadedFile ? 'bg-royal-30 border-royal' : 'border-royal-30'
                  }`}>
                    <div aria-hidden="true" className="absolute border border-royal-30 border-solid inset-0 pointer-events-none rounded-lg"/>
                    <div className="relative shrink-0 w-6 h-6">
                      <div className="absolute flex h-[22.627px] items-center justify-center left-[0.86px] top-px w-[22.627px]">
                        <div className="flex-none rotate-[45deg]">
                          <div className="h-5 relative w-3">
                            <div className="absolute inset-[-3.75%_-6.25%]">
                              <img alt="" className="block max-w-none w-full h-full" src={ASSETS.PLUS_ICON}/>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {hasUploadedFile && (
                      <div className="absolute -top-1 -right-1 bg-green-500 rounded-full w-3 h-3 flex items-center justify-center">
                        <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                      </div>
                    )}
                  </div>

                  {/* Suggestions Button (visual only) */}
                  <div className="box-border flex gap-0.5 h-8 items-center justify-center relative rounded-lg shrink-0 border-royal-30 w-8 md:w-auto md:pl-2 md:pr-4 py-1 p-1 md:p-1">
                    <div aria-hidden="true" className="absolute border border-royal-30 border-solid inset-0 pointer-events-none rounded-lg"/>
                    <div className="overflow-clip relative shrink-0 w-6 h-6">
                      <div className="absolute h-[22.25px] left-[1.25px] top-[0.25px] w-[21.5px]">
                        <img alt="" className="block max-w-none w-full h-full" src={ASSETS.MAGIC_WAND_ICON}/>
                      </div>
                    </div>
                    {/* Text label - hidden on mobile, shown on desktop */}
                    <div className="hidden md:flex capitalize flex-col font-inter font-semibold justify-center leading-[0] not-italic relative shrink-0 text-blue-black-90 text-[14px] text-center text-nowrap">
                      <p className="leading-[normal] whitespace-pre">Suggestions</p>
                    </div>
                  </div>

                  {/* Model Button (visual only) */}
                  <div className="box-border flex gap-0.5 h-8 items-center justify-center relative rounded-lg shrink-0 border-royal-30 w-8 md:w-auto md:pl-2 md:pr-4 py-1 p-1 md:p-1">
                    <div aria-hidden="true" className="absolute border border-royal-30 border-solid inset-0 pointer-events-none rounded-lg"/>
                    <div className="relative shrink-0 w-6 h-6 flex items-center justify-center">
                      <img alt="" className="block w-5 h-5" src={ASSETS.BRAIN_IMAGE}/>
                    </div>
                    {/* Text label - hidden on mobile, shown on desktop */}
                    <div className="hidden md:flex capitalize flex-col font-inter font-semibold justify-center leading-[0] not-italic relative shrink-0 text-blue-black-90 text-[14px] text-center text-nowrap">
                      <p className="leading-[normal] whitespace-pre">{formatModelValue(model)}</p>
                    </div>
                  </div>
                </div>

                {/* Filter Controls - Responsive (Static Chips) */}
                <div className="flex flex-wrap gap-2 items-center justify-start relative shrink-0">
                  {/* Mobile Filter Button */}
                  <div className="lg:hidden">
                    <div className="box-border flex gap-0.5 h-8 items-center justify-center px-3 py-0.5 relative rounded-lg shrink-0 border-royal-30">
                      <div aria-hidden="true" className="absolute border border-royal-30 border-solid inset-0 pointer-events-none rounded-lg"/>
                      <span className="capitalize font-inter font-semibold text-blue-black-90 text-[14px] text-center text-nowrap">
                        Appearance
                      </span>
                    </div>
                  </div>

                  {/* Desktop Filter Chips - Static Display */}
                  <div className="hidden lg:flex flex-wrap gap-2 items-center justify-start">
                    {/* Style Chip */}
                    <div className="bg-white box-border flex gap-0.5 h-8 items-center justify-center px-3 py-0.5 relative rounded-lg shrink-0">
                      <div aria-hidden="true" className={`absolute border border-solid inset-0 pointer-events-none rounded-lg ${
                        styleCount > 0 ? 'border-royal' : 'border-royal-30'
                      }`}/>
                      <span className={`font-inter font-semibold text-[14px] text-center text-nowrap ${
                        styleCount > 0 ? 'text-royal' : 'text-blue-black-90'
                      }`}>
                        {styleDisplayText}
                      </span>
                    </div>
                    {/* Age Chip */}
                    <div className="bg-white box-border flex gap-0.5 h-8 items-center justify-center px-3 py-0.5 relative rounded-lg shrink-0">
                      <div aria-hidden="true" className={`absolute border border-solid inset-0 pointer-events-none rounded-lg ${
                        isFilterSelected(filters.age) ? 'border-royal' : 'border-royal-30'
                      }`}/>
                      <span className={`font-inter font-semibold text-[14px] text-center text-nowrap ${
                        isFilterSelected(filters.age) ? 'text-royal' : 'text-blue-black-90'
                      }`}>
                        {formatFilterValue(filters.age)}
                      </span>
                    </div>
                    {/* Gender Chip */}
                    <div className="bg-white box-border flex gap-0.5 h-8 items-center justify-center px-3 py-0.5 relative rounded-lg shrink-0">
                      <div aria-hidden="true" className={`absolute border border-solid inset-0 pointer-events-none rounded-lg ${
                        isFilterSelected(filters.gender) ? 'border-royal' : 'border-royal-30'
                      }`}/>
                      <span className={`font-inter font-semibold text-[14px] text-center text-nowrap ${
                        isFilterSelected(filters.gender) ? 'text-royal' : 'text-blue-black-90'
                      }`}>
                        {formatFilterValue(filters.gender)}
                      </span>
                    </div>
                    {/* Hair Chip */}
                    <div className="bg-white box-border flex gap-0.5 h-8 items-center justify-center px-3 py-0.5 relative rounded-lg shrink-0">
                      <div aria-hidden="true" className={`absolute border border-solid inset-0 pointer-events-none rounded-lg ${
                        isFilterSelected(hairArray) ? 'border-royal' : 'border-royal-30'
                      }`}/>
                      <span className={`font-inter font-semibold text-[14px] text-center text-nowrap ${
                        isFilterSelected(hairArray) ? 'text-royal' : 'text-blue-black-90'
                      }`}>
                        {hairDisplayText}
                      </span>
                    </div>
                    {/* Background Chip */}
                    <div className="bg-white box-border flex gap-0.5 h-8 items-center justify-center px-3 py-0.5 relative rounded-lg shrink-0">
                      <div aria-hidden="true" className={`absolute border border-solid inset-0 pointer-events-none rounded-lg ${
                        isFilterSelected(filters.background) ? 'border-royal' : 'border-royal-30'
                      }`}/>
                      <span className={`font-inter font-semibold text-[14px] text-center text-nowrap ${
                        isFilterSelected(filters.background) ? 'text-royal' : 'text-blue-black-90'
                      }`}>
                        {formatFilterValue(filters.background)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Generate Button (visual only) */}
              <div className="relative">
                <div className="bg-gray-300 cursor-not-allowed box-border flex gap-1 items-center justify-center p-1 relative rounded-lg shrink-0 w-8 h-8">
                  <div className="relative shrink-0 w-6 h-6">
                    <div className="absolute h-[15.5px] left-[6.25px] top-[4.25px] w-[11.5px]">
                      <img 
                        alt="Generating..." 
                        className="block max-w-none w-full h-full opacity-50" 
                        src={ASSETS.ARROW_ICON}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoadingScreen;
